// Lightweight client-side blog store using localStorage.
// Includes seed data for demo and CRUD helpers used by both
// the public blog and the /admin section.

export type Category = {
  id: string;
  slug: string;
  name: string;
  description: string;
  color: string; // tailwind text color class
};

export type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string; // markdown-ish plain text
  cover: string; // gradient css string used as a "cover"
  categoryId: string;
  tags: string[];
  author: { name: string; avatar: string };
  status: "draft" | "published" | "scheduled";
  publishedAt: string; // ISO
  readingMinutes: number;
  views: number;
  likes: number;
  featured?: boolean;
};

export type Comment = {
  id: string;
  postId: string;
  name: string;
  email: string;
  body: string;
  status: "pending" | "approved" | "spam";
  createdAt: string;
};

const KEY = "trendy-blog-store-v1";

const SEED_CATEGORIES: Category[] = [
  {
    id: "c1",
    slug: "ai",
    name: "هوش مصنوعی",
    description: "تازه‌ترین مقالات AI و یادگیری ماشین",
    color: "text-purple",
  },
  {
    id: "c2",
    slug: "marketing",
    name: "بازاریابی دیجیتال",
    description: "استراتژی‌های جذب و تبدیل",
    color: "text-secondary-main",
  },
  {
    id: "c3",
    slug: "instagram",
    name: "اینستاگرام",
    description: "رشد و مدیریت پیج اینستاگرام",
    color: "text-orange",
  },
  {
    id: "c4",
    slug: "automation",
    name: "اتوماسیون",
    description: "خودکارسازی فرایندهای کسب‌وکار",
    color: "text-primary-main",
  },
  {
    id: "c5",
    slug: "case-study",
    name: "مطالعه موردی",
    description: "تجربه‌های موفق مشتریان",
    color: "text-blue",
  },
];

const lorem = `این یک مقاله نمونه است که برای نمایش طراحی صفحه استفاده می‌شود. ترندی اسمارت با تکیه بر مدل‌های زبانی فارسی، تجربه پاسخگویی به مشتریان را در تمام کانال‌های دیجیتال متحول می‌کند.

## چرا اتوماسیون مهم است؟

در دنیای امروز، سرعت پاسخگویی یکی از مهم‌ترین فاکتورهای رضایت مشتری است. مطالعات نشان می‌دهد که ۹۰٪ کاربران انتظار پاسخ کمتر از ۱۰ دقیقه را دارند.

## راهکار ما

با استفاده از موتور هوشمند پاسخ خودکار، می‌توانید:
- ۸۰٪ پیام‌های تکراری را به‌صورت خودکار پاسخ دهید
- زمان پاسخگویی را به کمتر از یک ثانیه برسانید
- تجربه‌ای یکپارچه در همه کانال‌ها ارائه کنید

## نتیجه‌گیری

اتوماسیون نه یک انتخاب، بلکه یک ضرورت برای کسب‌وکارهای امروزی است. ترندی اسمارت همراه شما در این مسیر است.`;

const SEED_POSTS: Post[] = [
  {
    id: "p1",
    slug: "ai-revolution-customer-support",
    title: "انقلاب هوش مصنوعی در پشتیبانی مشتری ایرانی",
    excerpt: "چگونه مدل‌های زبانی فارسی، تجربه پشتیبانی را برای کسب‌وکارهای ایرانی متحول می‌کنند.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(0 150 136), rgb(139 92 246))",
    categoryId: "c1",
    tags: ["AI", "پشتیبانی", "GPT"],
    author: { name: "آرمین حسینی", avatar: "AH" },
    status: "published",
    publishedAt: "2026-05-01T10:00:00Z",
    readingMinutes: 8,
    views: 2840,
    likes: 124,
    featured: true,
  },
  {
    id: "p2",
    slug: "instagram-growth-2026",
    title: "۱۰ ترفند رشد ارگانیک اینستاگرام در سال ۱۴۰۵",
    excerpt: "تکنیک‌های اثبات‌شده برای افزایش فالوور واقعی و تعامل بالا.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(254 150 74), rgb(255 152 0))",
    categoryId: "c3",
    tags: ["اینستاگرام", "رشد", "محتوا"],
    author: { name: "ندا رضوی", avatar: "ND" },
    status: "published",
    publishedAt: "2026-04-22T08:00:00Z",
    readingMinutes: 6,
    views: 5210,
    likes: 312,
    featured: true,
  },
  {
    id: "p3",
    slug: "automation-saves-time",
    title: "چگونه اتوماسیون، روزانه ۴ ساعت از وقت تیم شما را آزاد می‌کند",
    excerpt: "محاسبه دقیق ROI پیاده‌سازی پاسخ خودکار در یک تیم ۵ نفره پشتیبانی.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(0 150 136), rgb(20 184 166))",
    categoryId: "c4",
    tags: ["اتوماسیون", "بهره‌وری"],
    author: { name: "محمد کریمی", avatar: "MK" },
    status: "published",
    publishedAt: "2026-04-15T12:00:00Z",
    readingMinutes: 5,
    views: 1820,
    likes: 88,
  },
  {
    id: "p4",
    slug: "case-study-digikala",
    title: "مطالعه موردی: کاهش ۶۰٪ زمان پاسخ‌گویی در فروشگاه آنلاین",
    excerpt: "داستان موفقیت یک فروشگاه ۵۰ هزار کاربری با ترندی اسمارت.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(2 119 189), rgb(56 189 248))",
    categoryId: "c5",
    tags: ["مطالعه موردی", "ایکامرس"],
    author: { name: "سارا احمدی", avatar: "SA" },
    status: "published",
    publishedAt: "2026-04-08T09:00:00Z",
    readingMinutes: 10,
    views: 3450,
    likes: 201,
  },
  {
    id: "p5",
    slug: "content-marketing-funnel",
    title: "قیف بازاریابی محتوایی برای استارتاپ‌های B2B",
    excerpt: "از آگاهی تا تبدیل، یک نقشه راه عملی برای تیم‌های کوچک.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(255 152 0), rgb(245 158 11))",
    categoryId: "c2",
    tags: ["بازاریابی", "B2B", "استراتژی"],
    author: { name: "رضا مومنی", avatar: "RM" },
    status: "published",
    publishedAt: "2026-04-01T14:00:00Z",
    readingMinutes: 7,
    views: 1240,
    likes: 65,
  },
  {
    id: "p6",
    slug: "draft-future-of-ai",
    title: "[پیش‌نویس] آینده هوش مصنوعی فارسی",
    excerpt: "نگاهی به مسیر پیش روی LLMهای بومی.",
    content: lorem,
    cover: "linear-gradient(135deg, rgb(139 92 246), rgb(56 189 248))",
    categoryId: "c1",
    tags: ["AI", "آینده"],
    author: { name: "آرمین حسینی", avatar: "AH" },
    status: "draft",
    publishedAt: "2026-05-10T10:00:00Z",
    readingMinutes: 12,
    views: 0,
    likes: 0,
  },
];

const SEED_COMMENTS: Comment[] = [
  {
    id: "cm1",
    postId: "p1",
    name: "علی محمدی",
    email: "ali@example.com",
    body: "مقاله فوق‌العاده‌ای بود، ممنون!",
    status: "approved",
    createdAt: "2026-05-02T08:00:00Z",
  },
  {
    id: "cm2",
    postId: "p1",
    name: "زهرا کریمی",
    email: "z@example.com",
    body: "آیا برای زبان عربی هم پشتیبانی دارید؟",
    status: "pending",
    createdAt: "2026-05-03T10:00:00Z",
  },
  {
    id: "cm3",
    postId: "p2",
    name: "اسپم بات",
    email: "spam@x.com",
    body: "بهترین قیمت‌ها...",
    status: "spam",
    createdAt: "2026-05-04T11:00:00Z",
  },
  {
    id: "cm4",
    postId: "p3",
    name: "حسن جعفری",
    email: "h@example.com",
    body: "محاسبات ROI خیلی شفاف بود.",
    status: "approved",
    createdAt: "2026-05-05T09:00:00Z",
  },
];

type Store = { categories: Category[]; posts: Post[]; comments: Comment[] };

const SEED: Store = {
  categories: SEED_CATEGORIES,
  posts: SEED_POSTS,
  comments: SEED_COMMENTS,
};

function read(): Store {
  if (typeof window === "undefined") return SEED;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return SEED;
    return JSON.parse(raw) as Store;
  } catch {
    return SEED;
  }
}

function write(s: Store) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(s));
  window.dispatchEvent(new CustomEvent("blog-store-changed"));
}

export function useBlogStoreRef() {
  // Returns a snapshot reader; pair with subscribeBlogStore for reactivity.
  return read();
}

export function subscribeBlogStore(fn: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("blog-store-changed", fn);
  window.addEventListener("storage", fn);
  return () => {
    window.removeEventListener("blog-store-changed", fn);
    window.removeEventListener("storage", fn);
  };
}

// Posts
export const getPosts = () => read().posts;
export const getPublishedPosts = () =>
  read()
    .posts.filter((p) => p.status === "published")
    .sort((a, b) => +new Date(b.publishedAt) - +new Date(a.publishedAt));
export const getPost = (slug: string) => read().posts.find((p) => p.slug === slug);
export const getPostById = (id: string) => read().posts.find((p) => p.id === id);

export function savePost(p: Post) {
  const s = read();
  const idx = s.posts.findIndex((x) => x.id === p.id);
  if (idx >= 0) s.posts[idx] = p;
  else s.posts.unshift(p);
  write(s);
}
export function deletePost(id: string) {
  const s = read();
  s.posts = s.posts.filter((p) => p.id !== id);
  s.comments = s.comments.filter((c) => c.postId !== id);
  write(s);
}

// Categories
export const getCategories = () => read().categories;
export const getCategory = (slug: string) => read().categories.find((c) => c.slug === slug);
export function saveCategory(c: Category) {
  const s = read();
  const idx = s.categories.findIndex((x) => x.id === c.id);
  if (idx >= 0) s.categories[idx] = c;
  else s.categories.unshift(c);
  write(s);
}
export function deleteCategory(id: string) {
  const s = read();
  s.categories = s.categories.filter((c) => c.id !== id);
  write(s);
}

// Comments
export const getComments = () => read().comments;
export const getCommentsByPost = (postId: string) =>
  read().comments.filter((c) => c.postId === postId && c.status === "approved");
export function saveComment(c: Comment) {
  const s = read();
  const idx = s.comments.findIndex((x) => x.id === c.id);
  if (idx >= 0) s.comments[idx] = c;
  else s.comments.unshift(c);
  write(s);
}
export function deleteComment(id: string) {
  const s = read();
  s.comments = s.comments.filter((c) => c.id !== id);
  write(s);
}

export function resetStore() {
  write(SEED);
}

export const COVER_GRADIENTS = [
  "linear-gradient(135deg, rgb(0 150 136), rgb(139 92 246))",
  "linear-gradient(135deg, rgb(254 150 74), rgb(255 152 0))",
  "linear-gradient(135deg, rgb(0 150 136), rgb(20 184 166))",
  "linear-gradient(135deg, rgb(2 119 189), rgb(56 189 248))",
  "linear-gradient(135deg, rgb(255 152 0), rgb(245 158 11))",
  "linear-gradient(135deg, rgb(139 92 246), rgb(56 189 248))",
  "linear-gradient(135deg, rgb(229 57 53), rgb(255 152 0))",
];
